import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Bot, User, Send, Sparkles, PenLine, Loader2, ArrowLeft, ArrowRight,
  Mic, MicOff, CheckCircle, AlertCircle, Zap, RefreshCw, Upload, X, Image as ImageIcon,
  DollarSign, TrendingUp, Eye, Tag, Box, Languages, Wand2, FileText, Hash, Lightbulb
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import AIPricingSuggestion from '../components/AIPricingSuggestion';
import VehicleRegLookup from '../components/VehicleRegLookup';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAdAssistant = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { country, countryConfig } = useCountry();
  
  // Mode: 'select' | 'ai' | 'manual' | 'preview'
  const [mode, setMode] = useState('select');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [vehicleSpecs, setVehicleSpecs] = useState({});
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [language, setLanguage] = useState('en');
  
  // AI Chat State
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [questionsReady, setQuestionsReady] = useState(false);
  
  // Form Data State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    condition: '',
    phone: '',
    tags: [],
    category_fields: {}
  });
  const [isComplete, setIsComplete] = useState(false);
  const [seoTags, setSeoTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  
  // Image Upload State
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const imageInputRef = useRef(null);
  
  // Voice Input State
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  
  // Tone Selection State
  const [showToneButtons, setShowToneButtons] = useState(false);
  const [toneOptions, setToneOptions] = useState([]);
  const [availableTones, setAvailableTones] = useState([]);
  const [allTones, setAllTones] = useState([]);
  const [restrictedTones, setRestrictedTones] = useState([]);
  const [selectedTone, setSelectedTone] = useState(null);
  
  // AI Pricing State
  const [showPricingSuggestion, setShowPricingSuggestion] = useState(false);
  
  // AI Vision Analysis State
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [showImageAnalysis, setShowImageAnalysis] = useState(false);
  
  // AI Composer State
  const [composerGenerating, setComposerGenerating] = useState(false);
  const [composerContent, setComposerContent] = useState(null);
  const [selectedComposerTone, setSelectedComposerTone] = useState('professional');
  const [composerLanguage, setComposerLanguage] = useState('en');
  const [showConfirmPost, setShowConfirmPost] = useState(false);
  const [regeneratingField, setRegeneratingField] = useState(null);
  
  // Regeneration tracking
  const [regenerationsUsed, setRegenerationsUsed] = useState(0);
  const [freeRegensRemaining, setFreeRegensRemaining] = useState(3);
  const [extraRegenCost, setExtraRegenCost] = useState(10);
  const [extraRegenerations, setExtraRegenerations] = useState(0);
  const [regenerationBundles, setRegenerationBundles] = useState([]);
  
  // AI Flow Step: 'upload' | 'analyze' | 'compose' | 'edit' | 'review'
  const [aiFlowStep, setAiFlowStep] = useState('upload');
  
  // Membership State - Full Integration
  const [membershipData, setMembershipData] = useState(null);
  const [membershipType, setMembershipType] = useState('free');
  const [membershipExpiresAt, setMembershipExpiresAt] = useState(null);
  const [aiAdsLimit, setAiAdsLimit] = useState(5);
  const [aiAdsUsed, setAiAdsUsed] = useState(0);
  const [aiAdsRemaining, setAiAdsRemaining] = useState(5);
  const [canUseAI, setCanUseAI] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [membershipLoading, setMembershipLoading] = useState(true);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const token = localStorage.getItem('token');
  
  // All 13 tones - for display with lock icons
  const ALL_THIRTEEN_TONES = [
    "Professional", "Friendly", "Casual", "Formal", "Informal",
    "Urgent", "Luxury", "Bargain", "Optimistic", "Compassionate",
    "Exciting", "Assertive", "Persuasive"
  ];
  
  // Categories matching database structure
  const categories = [
    { id: 'electronics-computers', name: 'Electronics & Computers', icon: '💻' },
    { id: 'vehicles', name: 'Vehicles', icon: '🚗' },
    { id: 'auto-parts-accessories', name: 'Auto Parts & Accessories', icon: '🔧' },
    { id: 'property', name: 'Property', icon: '🏠' },
    { id: 'home-garden', name: 'Home & Garden', icon: '🏡' },
    { id: 'fashion-beauty', name: 'Fashion & Beauty', icon: '👗' },
    { id: 'sports-fitness-hobbies', name: 'Sports, Fitness & Hobbies', icon: '⚽' },
    { id: 'jobs', name: 'Jobs', icon: '💼' },
    { id: 'freelance-gigs', name: 'Freelance & Gigs', icon: '🎯' },
    { id: 'services-trades', name: 'Services & Trades', icon: '🛠️' },
    { id: 'business-industry', name: 'Business & Industry', icon: '🏭' },
    { id: 'food-agriculture', name: 'Food & Agriculture', icon: '🌾' },
    { id: 'education-tuition', name: 'Education & Tuition', icon: '📚' },
    { id: 'baby-kids-items', name: 'Baby & Kids Items', icon: '👶' },
    { id: 'pets-animals', name: 'Pets & Animals', icon: '🐾' },
    { id: 'musical-instruments', name: 'Musical Instruments', icon: '🎸' },
    { id: 'events-functions', name: 'Events & Functions', icon: '🎉' },
    { id: 'community-local-life', name: 'Community & Local Life', icon: '🤝' },
    { id: 'books-media', name: 'Books & Media', icon: '📖' },
    { id: 'other', name: 'Other', icon: '📦' }
  ];
  
  // Subcategory mapping by category
  const subcategoryMap = {
    'electronics-computers': [
      { slug: 'mobile-phones', name: 'Mobile Phones' },
      { slug: 'mobile-accessories', name: 'Mobile Accessories' },
      { slug: 'computers-laptops', name: 'Computers & Laptops' },
      { slug: 'computer-accessories', name: 'Computer Accessories' },
      { slug: 'tablets-e-readers', name: 'Tablets & E-readers' },
      { slug: 'tvs-home-entertainment', name: 'TVs & Home Entertainment' },
      { slug: 'audio-speakers-headphones', name: 'Audio (Speakers, Headphones)' },
      { slug: 'cameras-drones', name: 'Cameras & Drones' },
      { slug: 'gaming-consoles-games', name: 'Gaming Consoles & Games' },
      { slug: 'smart-devices-wearables', name: 'Smart Devices & Wearables' }
    ],
    'vehicles': [
      { slug: 'cars', name: 'Cars' },
      { slug: 'motorbikes-scooters', name: 'Motorbikes & Scooters' },
      { slug: 'three-wheelers', name: 'Three-Wheelers' },
      { slug: 'vans-buses', name: 'Vans & Buses' },
      { slug: 'trucks-lorries', name: 'Trucks & Lorries' },
      { slug: 'bicycles', name: 'Bicycles' },
      { slug: 'boats-watercraft', name: 'Boats & Watercraft' },
      { slug: 'vehicle-leasing', name: 'Vehicle Leasing' }
    ],
    'auto-parts-accessories': [
      { slug: 'car-parts', name: 'Car Parts' },
      { slug: 'motorbike-parts', name: 'Motorbike Parts' },
      { slug: 'tyres-wheels', name: 'Tyres & Wheels' },
      { slug: 'batteries', name: 'Batteries' },
      { slug: 'engine-parts', name: 'Engine Parts' },
      { slug: 'body-parts', name: 'Body Parts' },
      { slug: 'interior-accessories', name: 'Interior Accessories' },
      { slug: 'audio-gps', name: 'Audio & GPS' },
      { slug: 'tools-diagnostics', name: 'Tools & Diagnostics' }
    ],
    'property': [
      { slug: 'property-for-sale', name: 'Property for Sale' },
      { slug: 'property-for-rent', name: 'Property for Rent' },
      { slug: 'rooms-flatshares', name: 'Rooms & Flatshares' },
      { slug: 'commercial-property', name: 'Commercial Property' },
      { slug: 'land-plots', name: 'Land & Plots' },
      { slug: 'holiday-rentals', name: 'Holiday Rentals' },
      { slug: 'short-term-rentals', name: 'Short-term Rentals' },
      { slug: 'new-developments', name: 'New Developments' }
    ],
    'home-garden': [
      { slug: 'furniture', name: 'Furniture' },
      { slug: 'home-decor', name: 'Home Décor' },
      { slug: 'kitchenware', name: 'Kitchenware' },
      { slug: 'lighting', name: 'Lighting' },
      { slug: 'home-appliances', name: 'Home Appliances' },
      { slug: 'garden-tools', name: 'Garden Tools' },
      { slug: 'plants-outdoor', name: 'Plants & Outdoor' },
      { slug: 'home-improvement', name: 'Home Improvement' },
      { slug: 'storage-organisation', name: 'Storage & Organisation' }
    ],
    'fashion-beauty': [
      { slug: 'womens-clothing', name: "Women's Clothing" },
      { slug: 'mens-clothing', name: "Men's Clothing" },
      { slug: 'kids-clothing', name: "Kids' Clothing" },
      { slug: 'shoes-footwear', name: 'Shoes & Footwear' },
      { slug: 'bags-accessories', name: 'Bags & Accessories' },
      { slug: 'jewellery-watches', name: 'Jewellery & Watches' },
      { slug: 'beauty-personal-care', name: 'Beauty & Personal Care' },
      { slug: 'perfumes-cosmetics', name: 'Perfumes & Cosmetics' },
      { slug: 'traditional-wear', name: 'Traditional Wear' }
    ],
    'sports-fitness-hobbies': [
      { slug: 'sports-equipment', name: 'Sports Equipment' },
      { slug: 'fitness-gym', name: 'Fitness & Gym' },
      { slug: 'outdoor-camping', name: 'Outdoor & Camping' },
      { slug: 'cycling-equipment', name: 'Cycling Equipment' },
      { slug: 'fishing-hunting', name: 'Fishing & Hunting' },
      { slug: 'yoga-wellness', name: 'Yoga & Wellness' },
      { slug: 'board-games-puzzles', name: 'Board Games & Puzzles' },
      { slug: 'creative-hobbies', name: 'Creative Hobbies' }
    ],
    'jobs': [
      { slug: 'full-time-jobs', name: 'Full-time Jobs' },
      { slug: 'part-time-jobs', name: 'Part-time Jobs' },
      { slug: 'contract-jobs', name: 'Contract Jobs' },
      { slug: 'temporary-jobs', name: 'Temporary Jobs' },
      { slug: 'remote-jobs', name: 'Remote Jobs' },
      { slug: 'driver-delivery-jobs', name: 'Driver & Delivery Jobs' },
      { slug: 'hospitality-jobs', name: 'Hospitality Jobs' },
      { slug: 'skilled-labour-jobs', name: 'Skilled & Labour Jobs' }
    ],
    'freelance-gigs': [
      { slug: 'it-software-services', name: 'IT & Software Services' },
      { slug: 'design-creative', name: 'Design & Creative' },
      { slug: 'writing-translation', name: 'Writing & Translation' },
      { slug: 'digital-marketing', name: 'Digital Marketing' },
      { slug: 'consulting', name: 'Consulting' },
      { slug: 'tutoring-coaching', name: 'Tutoring & Coaching' },
      { slug: 'photography-video', name: 'Photography & Video' },
      { slug: 'short-term-gigs', name: 'Short-term Gigs' }
    ],
    'services-trades': [
      { slug: 'home-repairs-maintenance', name: 'Home Repairs & Maintenance' },
      { slug: 'plumbing-electrical', name: 'Plumbing & Electrical' },
      { slug: 'construction-masonry', name: 'Construction & Masonry' },
      { slug: 'cleaning-services', name: 'Cleaning Services' },
      { slug: 'moving-removal', name: 'Moving & Removal' },
      { slug: 'pest-control', name: 'Pest Control' },
      { slug: 'security-services', name: 'Security Services' },
      { slug: 'tailoring-alterations', name: 'Tailoring & Alterations' }
    ],
    'business-industry': [
      { slug: 'machinery-equipment', name: 'Machinery & Equipment' },
      { slug: 'industrial-tools', name: 'Industrial Tools' },
      { slug: 'manufacturing-supplies', name: 'Manufacturing Supplies' },
      { slug: 'office-equipment', name: 'Office Equipment' },
      { slug: 'wholesale-bulk-goods', name: 'Wholesale & Bulk Goods' },
      { slug: 'logistics-transport', name: 'Logistics & Transport' },
      { slug: 'franchises', name: 'Franchises' },
      { slug: 'business-for-sale', name: 'Business for Sale' }
    ],
    'food-agriculture': [
      { slug: 'fresh-produce', name: 'Fresh Produce' },
      { slug: 'livestock-poultry', name: 'Livestock & Poultry' },
      { slug: 'seeds-plants', name: 'Seeds & Plants' },
      { slug: 'fertilisers', name: 'Fertilisers' },
      { slug: 'farm-machinery', name: 'Farm Machinery' },
      { slug: 'animal-feed', name: 'Animal Feed' },
      { slug: 'organic-products', name: 'Organic Products' },
      { slug: 'fisheries', name: 'Fisheries' }
    ],
    'education-tuition': [
      { slug: 'private-tuition', name: 'Private Tuition' },
      { slug: 'online-courses', name: 'Online Courses' },
      { slug: 'professional-training', name: 'Professional Training' },
      { slug: 'language-classes', name: 'Language Classes' },
      { slug: 'it-coding-courses', name: 'IT & Coding Courses' },
      { slug: 'exam-preparation', name: 'Exam Preparation' },
      { slug: 'music-art-classes', name: 'Music & Art Classes' },
      { slug: 'educational-services', name: 'Educational Services' }
    ],
    'baby-kids-items': [
      { slug: 'baby-gear-furniture', name: 'Baby Gear & Furniture' },
      { slug: 'toys-games', name: 'Toys & Games' },
      { slug: 'baby-clothing', name: 'Baby Clothing' },
      { slug: 'kids-clothing-baby', name: 'Kids Clothing' },
      { slug: 'school-supplies', name: 'School Supplies' },
      { slug: 'prams-strollers', name: 'Prams & Strollers' },
      { slug: 'car-seats', name: 'Car Seats' },
      { slug: 'maternity-items', name: 'Maternity Items' }
    ],
    'pets-animals': [
      { slug: 'dogs', name: 'Dogs' },
      { slug: 'cats', name: 'Cats' },
      { slug: 'birds', name: 'Birds' },
      { slug: 'fish-aquariums', name: 'Fish & Aquariums' },
      { slug: 'livestock', name: 'Livestock' },
      { slug: 'pet-food', name: 'Pet Food' },
      { slug: 'pet-accessories', name: 'Pet Accessories' },
      { slug: 'pet-services', name: 'Pet Services' }
    ],
    'musical-instruments': [
      { slug: 'guitars', name: 'Guitars' },
      { slug: 'keyboards-pianos', name: 'Keyboards & Pianos' },
      { slug: 'drums-percussion', name: 'Drums & Percussion' },
      { slug: 'wind-instruments', name: 'Wind Instruments' },
      { slug: 'string-instruments', name: 'String Instruments' },
      { slug: 'audio-equipment', name: 'Audio Equipment' },
      { slug: 'recording-gear', name: 'Recording Gear' },
      { slug: 'music-accessories', name: 'Music Accessories' }
    ],
    'events-functions': [
      { slug: 'wedding-services', name: 'Wedding Services' },
      { slug: 'event-venues', name: 'Event Venues' },
      { slug: 'catering-services', name: 'Catering Services' },
      { slug: 'photography-videography', name: 'Photography & Videography' },
      { slug: 'event-decoration', name: 'Event Decoration' },
      { slug: 'djs-bands', name: 'DJs & Bands' },
      { slug: 'party-equipment-hire', name: 'Party Equipment Hire' },
      { slug: 'event-planning', name: 'Event Planning' }
    ],
    'community-local-life': [
      { slug: 'local-groups-clubs', name: 'Local Groups & Clubs' },
      { slug: 'classes-workshops', name: 'Classes & Workshops' },
      { slug: 'lost-found', name: 'Lost & Found' },
      { slug: 'volunteers', name: 'Volunteers' },
      { slug: 'charity-donations', name: 'Charity & Donations' },
      { slug: 'notices-announcements', name: 'Notices & Announcements' },
      { slug: 'ride-sharing', name: 'Ride Sharing' },
      { slug: 'community-help', name: 'Community Help' }
    ],
    'books-media': [
      { slug: 'books', name: 'Books' },
      { slug: 'magazines', name: 'Magazines' },
      { slug: 'educational-media', name: 'Educational Media' },
      { slug: 'comics-graphic-novels', name: 'Comics & Graphic Novels' },
      { slug: 'cds-dvds', name: 'CDs & DVDs' },
      { slug: 'digital-media', name: 'Digital Media' },
      { slug: 'collectibles', name: 'Collectibles' },
      { slug: 'stationery', name: 'Stationery' }
    ],
    'other': [
      { slug: 'miscellaneous-items', name: 'Miscellaneous Items' },
      { slug: 'clearance-free-items', name: 'Clearance & Free Items' },
      { slug: 'uncategorised-listings', name: 'Uncategorised Listings' },
      { slug: 'bulk-lots', name: 'Bulk Lots' },
      { slug: 'giveaways', name: 'Giveaways' }
    ]
  };
  
  // Check speech recognition support
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setSpeechSupported(true);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true; // Keep listening for continuous speech
      recognitionRef.current.interimResults = true; // Show real-time transcription
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setInputMessage(prev => {
            const newText = prev ? prev + ' ' + finalTranscript.trim() : finalTranscript.trim();
            return newText;
          });
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          // Don't show error for no-speech, just stop listening
        } else if (event.error === 'not-allowed') {
          toast({
            title: 'Microphone Access Denied',
            description: 'Please allow microphone access in your browser settings',
            variant: 'destructive'
          });
        }
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [toast]);
  
  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Check authentication
  useEffect(() => {
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to post an advertisement',
        variant: 'destructive'
      });
      navigate('/login', { state: { from: '/post-ad' } });
    } else {
      // Get user phone from stored user data
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.phone) {
        setFormData(prev => ({ ...prev, phone: userData.phone }));
      }
    }
  }, [token]);
  
  // Fetch membership data on mount
  useEffect(() => {
    const fetchMembershipData = async () => {
      if (!token) return;
      
      setMembershipLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/api/membership/ai-access`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data) {
          const data = response.data;
          setMembershipData(data);
          setMembershipType(data.membership_type || 'free');
          setMembershipExpiresAt(data.membership_expires_at);
          setAiAdsLimit(data.ai_ads_limit || 5);
          setAiAdsUsed(data.ai_ads_used || 0);
          setAiAdsRemaining(data.ai_ads_remaining || 5);
          setCanUseAI(data.can_use_ai !== false);
          
          // Set available and restricted tones from API
          setAvailableTones(data.available_tones || ['Professional', 'Friendly']);
          setRestrictedTones(data.restricted_tones || []);
          setAllTones(data.all_tones || ALL_THIRTEEN_TONES);
          
          // Set regeneration limits
          setFreeRegensRemaining(data.free_regens_per_ad || 3);
          setExtraRegenCost(data.extra_regen_cost || 10);
          setExtraRegenerations(data.extra_regenerations || 0);
          setRegenerationBundles(data.regeneration_bundles || []);
          
          console.log('Membership loaded:', data.membership_type, 'Tones:', data.available_tones?.length);
        }
      } catch (error) {
        console.error('Error fetching membership data:', error);
        // Set defaults for free tier
        setMembershipType('free');
        setAvailableTones(['Professional', 'Friendly']);
        setRestrictedTones(['Casual', 'Formal', 'Informal', 'Urgent', 'Luxury', 'Bargain', 'Optimistic', 'Compassionate', 'Exciting', 'Assertive', 'Persuasive']);
        setFreeRegensRemaining(2);
      } finally {
        setMembershipLoading(false);
      }
    };
    
    fetchMembershipData();
  }, [token]);
  
  // Update subcategories when category changes
  useEffect(() => {
    if (category && subcategoryMap[category]) {
      setSubcategories(subcategoryMap[category]);
      setSubcategory(''); // Reset subcategory when category changes
    } else {
      setSubcategories([]);
      setSubcategory('');
    }
  }, [category]);
  
  // Pre-populate question answers when questions change (for N/A defaults and AI auto-fill)
  useEffect(() => {
    if (messages.length > 0 && selectedTone) {
      const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant' && !m.isComplete);
      if (lastAssistantMessage) {
        const questions = extractQuestions(lastAssistantMessage.content);
        if (questions.length > 0) {
          const initialAnswers = {};
          questions.forEach((q, idx) => {
            // Only set if not already answered by user
            if (questionAnswers[q.id] === undefined) {
              let autoFillValue = '';
              const qLower = q.text.toLowerCase();
              
              // Check AI analysis for auto-fill
              if (imageAnalysis) {
                if (qLower.includes('brand') && imageAnalysis.brands?.length > 0) {
                  autoFillValue = imageAnalysis.brands[0];
                } else if (qLower.includes('model') && imageAnalysis.model) {
                  autoFillValue = imageAnalysis.model;
                } else if ((qLower.includes('color') || qLower.includes('colour')) && imageAnalysis.colors?.length > 0) {
                  autoFillValue = imageAnalysis.colors[0];
                } else if (qLower.includes('condition') && imageAnalysis.condition) {
                  autoFillValue = imageAnalysis.condition;
                }
              }
              
              // Set N/A for optional fields (Q6+), default values for common questions
              const isMandatory = idx < 5;
              if (!isMandatory) {
                // Provide sensible defaults for optional questions that AI will accept
                if (qLower.includes('battery')) autoFillValue = '90%';
                else if (qLower.includes('screen')) autoFillValue = 'Good condition';
                else if (qLower.includes('box') || qLower.includes('accessories')) autoFillValue = 'Yes';
                else if (qLower.includes('warranty')) autoFillValue = 'No warranty';
                else if (qLower.includes('imei')) autoFillValue = 'Yes';
                else if (qLower.includes('reason')) autoFillValue = 'Upgrading';
                else if (qLower.includes('year') || qLower.includes('purchase')) autoFillValue = '2023';
                else if (qLower.includes('description') || qLower.includes('detailed')) autoFillValue = 'Well maintained item in good condition';
                else if (qLower.includes('price type') || qLower.includes('negotiable')) autoFillValue = 'Negotiable';
                else if (qLower.includes('availability') || qLower.includes('available')) autoFillValue = 'Available now';
                else autoFillValue = 'Not specified';
              }
              initialAnswers[q.id] = autoFillValue || (isMandatory ? '' : 'Not specified');
            }
          });
          
          if (Object.keys(initialAnswers).length > 0) {
            setQuestionAnswers(prev => ({ ...prev, ...initialAnswers }));
          }
        }
      }
    }
  }, [messages, selectedTone, imageAnalysis]);
  
  // Helper function to parse markdown bold (**text**) to actual bold
  const parseMarkdownBold = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };
  
  // Extract questions from AI message for input fields
  const extractQuestions = (content) => {
    const lines = content.split('\n');
    const questions = [];
    lines.forEach((line, index) => {
      if (line.includes('[QUESTION')) {
        const questionText = line.replace(/\[QUESTION \d+\]/g, '').trim();
        const match = line.match(/\[QUESTION (\d+)\]/);
        const questionNum = match ? parseInt(match[1]) : index + 1;
        questions.push({
          id: questionNum,
          text: questionText,
          originalLine: line
        });
      }
    });
    return questions;
  };
  
  // Handle question answer change
  const handleAnswerChange = (questionId, value) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  
  // Submit all answers at once
  const submitAllAnswers = async () => {
    // Get all answers including N/A (which means "not applicable")
    const answeredQuestions = Object.entries(questionAnswers)
      .filter(([_, value]) => value && value.trim() !== '' && value.toLowerCase() !== 'skip')
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([id, value]) => `${id}. ${value}`)
      .join(', ');  // Use comma instead of newline to avoid JSON issues
    
    if (!answeredQuestions) {
      toast({
        title: 'No Answers',
        description: 'Please answer at least the required questions before submitting',
        variant: 'destructive'
      });
      return;
    }
    
    // Check if we have all required answers (at least first 5)
    const requiredAnswered = [1, 2, 3, 4, 5].every(id => 
      questionAnswers[id] && questionAnswers[id].trim() !== '' && questionAnswers[id].toLowerCase() !== 'n/a'
    );
    
    if (!requiredAnswered) {
      toast({
        title: 'Missing Required Fields',
        description: 'Please fill in all required fields (Q1-Q5) before generating',
        variant: 'destructive'
      });
      return;
    }
    
    // Format the answer for AI - all in one message
    const formattedAnswer = `Here are all my answers: ${answeredQuestions}`;
    
    // Don't clear question answers - keep them for reference
    setQuestionsReady(false);
    
    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: formattedAnswer }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-ads/chat`,
        { conversation_id: conversationId, message: formattedAnswer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if this is a complete advertisement
      let isAdComplete = response.data.is_complete;
      let currentResponse = response;
      
      // If AI asks for more questions, auto-respond with remaining N/A answers
      let retryCount = 0;
      while (!isAdComplete && retryCount < 3) {
        retryCount++;
        
        // Send remaining answers as N/A
        const remainingAnswers = "11. Upgrading, 12. 2023, 13. Well maintained item, 14. Negotiable, 15. Available now";
        
        try {
          currentResponse = await axios.post(
            `${API_URL}/api/ai-ads/chat`,
            { conversation_id: conversationId, message: `Here are the remaining answers: ${remainingAnswers}` },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          isAdComplete = currentResponse.data.is_complete;
        } catch (e) {
          console.error('Follow-up error:', e);
          break;
        }
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: currentResponse.data.message,
        provider: currentResponse.data.provider,
        isComplete: isAdComplete
      }]);
      
      setCurrentProvider(currentResponse.data.provider);
      
      if (currentResponse.data.form_data) {
        setFormData(prev => ({
          ...prev,
          ...currentResponse.data.form_data
        }));
        
        // Generate SEO tags from the form data
        if (currentResponse.data.form_data.title) {
          const generatedTags = [
            subcategory?.replace(/-/g, ' '),
            category?.replace(/-/g, ' '),
            ...currentResponse.data.form_data.title.split(' ').filter(w => w.length > 3).slice(0, 3),
            formData.condition || 'used',
            'sri lanka',
            'buy',
            'sell'
          ].filter(Boolean).map(t => t.toLowerCase());
          setSeoTags([...new Set(generatedTags)]);
          setFormData(prev => ({ ...prev, tags: [...new Set(generatedTags)] }));
        }
      }
      
      if (isAdComplete) {
        setIsComplete(true);
      }
    } catch (error) {
      console.error('Error sending answers:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Image upload handler
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    if (images.length + files.length > 5) {
      toast({
        title: 'Too Many Images',
        description: 'You can upload maximum 5 images',
        variant: 'destructive'
      });
      return;
    }
    
    setUploadingImages(true);
    
    try {
      const formDataUpload = new FormData();
      // API expects field name 'images' for multiple files
      files.forEach(file => {
        formDataUpload.append('images', file);
      });
      
      const response = await axios.post(
        `${API_URL}/api/images/upload`,
        formDataUpload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      // Extract URLs from response
      const newImageUrls = response.data.images.map(img => img.url);
      setImages(prev => [...prev, ...newImageUrls]);
      
      toast({
        title: 'Images Uploaded',
        description: `${files.length} image(s) uploaded successfully`
      });
      
      // Trigger AI Vision Analysis for the first uploaded image (non-blocking)
      if (files.length > 0) {
        analyzeImageWithAI(files[0]).catch(err => {
          console.warn('AI Vision analysis failed, continuing without it:', err);
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload images',
        variant: 'destructive'
      });
    } finally {
      setUploadingImages(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };
  
  // AI Vision Analysis function
  const analyzeImageWithAI = async (imageFile) => {
    setAnalyzingImage(true);
    setShowImageAnalysis(true);
    
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      if (category) {
        formData.append('category', category);
      }
      
      const response = await axios.post(
        `${API_URL}/api/ai/vision/analyze-for-ad`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const analysis = response.data;
      setImageAnalysis(analysis);
      
      // Auto-fill detected data into form
      if (analysis.detected_data) {
        const { brand, condition, suggested_title, tags } = analysis.detected_data;
        
        // Update form with detected data
        setFormData(prev => ({
          ...prev,
          ...(brand && !prev.category_fields?.brand && { 
            category_fields: { ...prev.category_fields, brand } 
          }),
          ...(condition && !prev.condition && { condition }),
          ...(suggested_title && !prev.title && { title: suggested_title })
        }));
        
        toast({
          title: '✨ AI Analysis Complete',
          description: `Detected: ${brand || 'Unknown'} - ${condition || 'Item analyzed'}`,
        });
      }
    } catch (error) {
      console.error('AI Vision analysis error:', error);
      toast({
        title: 'AI Analysis',
        description: 'Could not analyze image. You can still proceed manually.',
        variant: 'default'
      });
    } finally {
      setAnalyzingImage(false);
    }
  };
  
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  
  const startAIConversation = async () => {
    if (!category) {
      toast({
        title: 'Select Category',
        description: 'Please select a category first',
        variant: 'destructive'
      });
      return;
    }
    
    if (!subcategory) {
      toast({
        title: 'Select Subcategory',
        description: 'Please select a subcategory to get specific questions',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-ads/start`,
        { category, subcategory, language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if there's an error (no AI credits)
      if (response.data.error) {
        toast({
          title: 'AI Credits Exhausted',
          description: response.data.message,
          variant: 'destructive'
        });
        setMode('select');
        return;
      }
      
      setConversationId(response.data.conversation_id);
      setMessages([{
        role: 'assistant',
        content: response.data.message,
        showToneButtons: response.data.show_tone_buttons || false
      }]);
      
      // Set tone options if provided
      if (response.data.tone_options) {
        setToneOptions(response.data.tone_options);
        setAvailableTones(response.data.available_tones || response.data.tone_options);
        setRestrictedTones(response.data.restricted_tones || []);
        setShowToneButtons(true);
      }
      
      // Set all tones for display
      if (response.data.all_tones) {
        setAllTones(response.data.all_tones);
      }
      
      // Set membership info
      if (response.data.membership_type) {
        setMembershipType(response.data.membership_type);
      }
      if (response.data.ai_ads_remaining !== undefined) {
        setAiAdsRemaining(response.data.ai_ads_remaining);
      }
      
      // Set regeneration info
      if (response.data.free_regens_per_ad !== undefined) {
        setFreeRegensRemaining(response.data.free_regens_per_ad);
      }
      if (response.data.extra_regen_cost !== undefined) {
        setExtraRegenCost(response.data.extra_regen_cost);
      }
      
      setMode('ai');
      setAiFlowStep('upload'); // Start with image upload
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast({
        title: 'Error',
        description: 'Failed to start AI assistant. Try manual mode.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle tone button click
  const handleToneSelect = async (tone) => {
    if (!conversationId || isLoading) return;
    
    // Check if tone is restricted
    if (restrictedTones.includes(tone)) {
      toast({
        title: 'Tone Restricted',
        description: `"${tone}" is not available in your membership. Upgrade to access more tones.`,
        variant: 'destructive'
      });
      return;
    }
    
    setSelectedTone(tone);
    setShowToneButtons(false);
    setMessages(prev => [...prev, { role: 'user', content: `Selected tone: ${tone}` }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-ads/chat`,
        { conversation_id: conversationId, message: tone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.message,
        provider: response.data.provider,
        isQuestion: response.data.is_question
      }]);
      
      setCurrentProvider(response.data.provider);
      
      if (response.data.form_data) {
        setFormData(prev => ({
          ...prev,
          ...response.data.form_data
        }));
      }
      
      if (response.data.is_complete) {
        setIsComplete(true);
      }
    } catch (error) {
      console.error('Error selecting tone:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, there was an error. Please try again.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle regeneration of specific fields
  const handleRegenerateField = async (field) => {
    setRegeneratingField(field);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/composer/regenerate-field`,
        {
          field,
          current_content: {
            title: formData.title,
            description: formData.description,
            tags: formData.tags || seoTags || []
          },
          product_info: {
            category,
            subcategory,
            images: images
          },
          tone: (selectedTone || selectedComposerTone || 'casual').toLowerCase(),
          language
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        if (field === 'title' && response.data.alternatives?.length > 0) {
          // Pick the first alternative for now
          setFormData(prev => ({ ...prev, title: response.data.alternatives[0] }));
        } else if (field === 'description' && response.data.description) {
          setFormData(prev => ({ ...prev, description: response.data.description }));
        } else if (field === 'tags' && response.data.tags) {
          setFormData(prev => ({ ...prev, tags: response.data.tags }));
          setSeoTags(response.data.tags);
        }
        
        setRegenerationsUsed(prev => prev + 1);
        if (freeRegensRemaining > 0) {
          setFreeRegensRemaining(prev => prev - 1);
        }
        
        toast({
          title: 'Regenerated!',
          description: `New ${field} generated successfully.`
        });
      } else {
        throw new Error(response.data.error || 'Regeneration failed');
      }
    } catch (error) {
      console.error('Regeneration error:', error);
      toast({
        title: 'Error',
        description: `Failed to regenerate ${field}. Please try again.`,
        variant: 'destructive'
      });
    } finally {
      setRegeneratingField(null);
    }
  };
  
  // Handle posting the ad
  const handlePostAd = async () => {
    if (!formData.title || !formData.description) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in at least title and description.',
        variant: 'destructive'
      });
      return;
    }
    
    if (!formData.price || parseInt(formData.price) <= 0) {
      toast({
        title: 'Price Required',
        description: 'Please enter a valid price for your ad.',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Use JSON endpoint for AI-created ads with pre-uploaded images
      const adData = {
        title: formData.title,
        description: formData.description,
        category: category || 'other',
        price: parseInt(formData.price) || 0,
        location: formData.location || 'Sri Lanka',
        condition: formData.condition || 'Good',
        negotiable: true,
        phone: formData.phone || '',
        images: images || [], // Already uploaded image paths
        featured: false,
        promoted: false
      };

      if (subcategory) adData.subcategory = subcategory;

      // Vehicle specs (only for vehicles category, only if seller filled anything)
      if (category === 'vehicles' && vehicleSpecs && Object.keys(vehicleSpecs).some(k => vehicleSpecs[k] !== '' && vehicleSpecs[k] != null)) {
        adData.vehicle_details = vehicleSpecs;
      }
      
      const response = await axios.post(
        `${API_URL}/api/listings/create-json`,
        adData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (response.data.id) {
        toast({
          title: 'Ad Posted Successfully! 🎉',
          description: 'Your ad is now live.',
        });
        setShowConfirmPost(false);
        navigate('/my-ads');
      } else {
        throw new Error('Failed to create listing');
      }
    } catch (error) {
      console.error('Error posting ad:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to post ad. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-ads/chat`,
        { conversation_id: conversationId, message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if this is a complete advertisement
      const isAdComplete = response.data.is_complete;
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.message,
        provider: response.data.provider,
        isComplete: isAdComplete  // Flag for special styling
      }]);
      
      setCurrentProvider(response.data.provider);
      
      if (response.data.form_data) {
        setFormData(prev => ({
          ...prev,
          ...response.data.form_data
        }));
      }
      
      if (isAdComplete) {
        setIsComplete(true);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or switch to manual mode.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Supported voice languages
  const voiceLanguages = {
    'en': { code: 'en-US', name: 'English', flag: '🇬🇧' },
    'si': { code: 'si-LK', name: 'සිංහල (Sinhala)', flag: '🇱🇰' },
    'ta': { code: 'ta-LK', name: 'தமிழ் (Tamil)', flag: '🇱🇰' },
    'hi': { code: 'hi-IN', name: 'हिंदी (Hindi)', flag: '🇮🇳' },
    'ar': { code: 'ar-SA', name: 'العربية (Arabic)', flag: '🇸🇦' },
    'zh': { code: 'zh-CN', name: '中文 (Chinese)', flag: '🇨🇳' },
    'ja': { code: 'ja-JP', name: '日本語 (Japanese)', flag: '🇯🇵' },
    'ko': { code: 'ko-KR', name: '한국어 (Korean)', flag: '🇰🇷' },
    'fr': { code: 'fr-FR', name: 'Français (French)', flag: '🇫🇷' },
    'de': { code: 'de-DE', name: 'Deutsch (German)', flag: '🇩🇪' },
    'es': { code: 'es-ES', name: 'Español (Spanish)', flag: '🇪🇸' },
  };

  const toggleVoiceInput = () => {
    if (!speechSupported) {
      toast({
        title: 'Not Supported',
        description: 'Voice input is not supported in your browser. Try Chrome or Edge.',
        variant: 'destructive'
      });
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      toast({
        title: 'Voice Input Stopped',
        description: 'Microphone turned off',
      });
    } else {
      try {
        // Set language for recognition
        const voiceLang = voiceLanguages[language] || voiceLanguages['en'];
        recognitionRef.current.lang = voiceLang.code;
        recognitionRef.current?.start();
        setIsListening(true);
        toast({
          title: `🎤 Listening in ${voiceLang.name}...`,
          description: 'Speak now. Click the mic button again to stop.',
        });
      } catch (error) {
        console.error('Speech recognition error:', error);
        toast({
          title: 'Microphone Error',
          description: 'Could not start voice input. Please check microphone permissions.',
          variant: 'destructive'
        });
      }
    }
  };

  // AI Composer: Generate ad content
  const generateAIContent = async () => {
    if (!category) {
      toast({
        title: 'Category Required',
        description: 'Please select a category first',
        variant: 'destructive'
      });
      return;
    }
    
    setComposerGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/composer/generate`,
        {
          category: category,
          subcategory: subcategory || null,
          condition: formData.condition || null,
          price: formData.price ? parseFloat(formData.price) : null,
          location: formData.location || null,
          brand: formData.category_fields?.brand || null,
          model: formData.category_fields?.model || null,
          features: formData.category_fields?.features || null,
          user_description: formData.description || null,
          tone: selectedComposerTone,
          language: composerLanguage,
          include_seo: true,
          image_analysis: imageAnalysis
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setComposerContent(response.data);
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          description: response.data.description || prev.description
        }));
        toast({
          title: '✨ Content Generated!',
          description: 'AI has created your ad content. Review and edit if needed.'
        });
      } else {
        throw new Error(response.data.error || 'Generation failed');
      }
    } catch (error) {
      console.error('AI content generation error:', error);
      toast({
        title: 'Generation Error',
        description: error.response?.data?.detail || 'Failed to generate content',
        variant: 'destructive'
      });
    } finally {
      setComposerGenerating(false);
    }
  };

  // AI Composer: Regenerate specific field
  const regenerateField = async (field) => {
    setRegeneratingField(field);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/composer/regenerate-field`,
        {
          field: field,
          current_content: {
            title: formData.title,
            description: formData.description,
            tags: composerContent?.tags || []
          },
          product_info: {
            category: category,
            subcategory: subcategory
          },
          tone: selectedComposerTone,
          language: composerLanguage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        if (field === 'title' && response.data.alternatives) {
          // Show alternatives for title
          toast({
            title: 'Alternative Titles',
            description: 'Select one of the alternatives below'
          });
          setComposerContent(prev => ({
            ...prev,
            title_alternatives: response.data.alternatives
          }));
        } else if (field === 'description' && response.data.description) {
          setFormData(prev => ({
            ...prev,
            description: response.data.description
          }));
          toast({ title: 'Description Updated!' });
        } else if (field === 'tags' && response.data.tags) {
          setComposerContent(prev => ({
            ...prev,
            tags: response.data.tags
          }));
          toast({ title: 'Tags Regenerated!' });
        }
      }
    } catch (error) {
      console.error('Regeneration error:', error);
      toast({
        title: 'Error',
        description: 'Failed to regenerate',
        variant: 'destructive'
      });
    } finally {
      setRegeneratingField(null);
    }
  };

  // AI Composer: Translate content
  const translateContent = async (targetLang) => {
    if (!formData.title || !formData.description) {
      toast({
        title: 'Content Required',
        description: 'Generate content first before translating',
        variant: 'destructive'
      });
      return;
    }
    
    setComposerGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/composer/translate`,
        {
          content: {
            title: formData.title,
            description: formData.description,
            highlights: composerContent?.highlights || [],
            call_to_action: composerContent?.call_to_action || ''
          },
          target_language: targetLang
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.translated) {
        setFormData(prev => ({
          ...prev,
          title: response.data.translated.title || prev.title,
          description: response.data.translated.description || prev.description
        }));
        setComposerContent(prev => ({
          ...prev,
          highlights: response.data.translated.highlights || prev?.highlights,
          call_to_action: response.data.translated.call_to_action || prev?.call_to_action
        }));
        setComposerLanguage(targetLang);
        toast({
          title: '🌍 Translated!',
          description: `Content translated to ${targetLang === 'si' ? 'Sinhala' : 'Tamil'}`
        });
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast({
        title: 'Translation Error',
        description: 'Failed to translate content',
        variant: 'destructive'
      });
    } finally {
      setComposerGenerating(false);
    }
  };

  // Handle confirm before post
  const handleConfirmPost = () => {
    if (!formData.title || !formData.description) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in title and description',
        variant: 'destructive'
      });
      return;
    }
    setShowConfirmPost(true);
  };
  
  const submitAd = async () => {
    if (!formData.title || !formData.description) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in title and description',
        variant: 'destructive'
      });
      return;
    }
    
    if (!formData.phone) {
      toast({
        title: 'Phone Required',
        description: 'Please add your phone number',
        variant: 'destructive'
      });
      return;
    }
    
    if (images.length === 0) {
      toast({
        title: 'Images Required',
        description: 'Please upload at least one image',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // Submit to existing listing creation endpoint
      const listingData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        location: formData.location || 'Sri Lanka',
        category: subcategory || category,  // Use subcategory if available
        condition: formData.condition || 'Used - Good',
        phone: formData.phone,
        images: images,
        negotiable: true,
        ...formData.category_fields
      };
      
      const response = await axios.post(
        `${API_URL}/api/listings/create-json`,
        listingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success!',
        description: 'Your advertisement has been posted successfully!'
      });
      
      navigate(`/listing/${response.data.id}`);
    } catch (error) {
      console.error('Error submitting ad:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to post advertisement',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Mode Selection Screen
  if (mode === 'select') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4">Post Your Advertisement</h1>
            <p className="text-blue-100 text-lg">Choose how you want to create your ad</p>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Category Selection */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>1. Select Category</CardTitle>
              <CardDescription>What are you selling or offering?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`p-3 rounded-lg border-2 transition-all text-center hover:shadow-md ${
                      category === cat.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">{cat.icon}</span>
                    <span className="font-medium text-sm">{cat.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Subcategory Selection - Only show when category is selected */}
          {category && subcategories.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>2. Select Subcategory</CardTitle>
                <CardDescription>
                  Choose the specific type to get tailored questions for your ad
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {subcategories.map((sub) => (
                    <button
                      key={sub.slug}
                      onClick={() => setSubcategory(sub.slug)}
                      className={`p-3 rounded-lg border-2 transition-all text-left hover:shadow-md ${
                        subcategory === sub.slug 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <span className="font-medium text-sm">{sub.name}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Language Selection */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{category && subcategories.length > 0 ? '3' : '2'}. Select Language</CardTitle>
              <CardDescription>AI will communicate in your preferred language</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {[
                  { code: 'en', name: 'English', flag: '🇬🇧' },
                  { code: 'si', name: 'සිංහල', flag: '🇱🇰' },
                  { code: 'ta', name: 'தமிழ்', flag: '🇱🇰' }
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-6 py-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                      language === lang.code 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle>{category && subcategories.length > 0 ? '4' : '3'}. Choose Your Mode</CardTitle>
              <CardDescription>How would you like to create your advertisement?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* AI Mode */}
                <button
                  onClick={startAIConversation}
                  disabled={!category || !subcategory || isLoading}
                  className="p-6 rounded-xl border-2 border-purple-200 hover:border-purple-500 hover:shadow-lg transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">AI Assistant</h3>
                      <Badge className="bg-purple-100 text-purple-700">Recommended</Badge>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Chat with our AI assistant who will ask you {subcategory ? 'specific questions about your ' + subcategories.find(s => s.slug === subcategory)?.name : 'questions'} and automatically create a professional advertisement.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Interactive Q&A conversation
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Auto-generates title & description
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Voice input supported
                    </li>
                  </ul>
                  {isLoading && (
                    <div className="mt-4 flex items-center gap-2 text-purple-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Starting AI Assistant...
                    </div>
                  )}
                </button>
                
                {/* Manual Mode */}
                <button
                  onClick={() => setMode('manual')}
                  disabled={!category || !subcategory}
                  className="p-6 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                      <PenLine className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Manual Entry</h3>
                      <Badge variant="outline">Traditional</Badge>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Fill out the form yourself with complete control over every field.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Full control over content
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Direct form filling
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      No AI involved
                    </li>
                  </ul>
                </button>
              </div>
              
              {/* Helper message if category/subcategory not selected */}
              {(!category || !subcategory) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    {!category 
                      ? '👆 Please select a category first' 
                      : '👆 Please select a subcategory to continue'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // AI Chat Mode

  // AI Chat Mode - Combined interface with Image Upload, Questions, and Preview
  if (mode === 'ai') {
    // Extract questions from the latest AI message
    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant' && !m.isComplete);
    const questions = lastAssistantMessage ? extractQuestions(lastAssistantMessage.content) : [];
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => {
                setMode('select');
                setMessages([]);
                setConversationId(null);
                setIsComplete(false);
                setImages([]);
                setImageAnalysis(null);
              }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                AI Ad Assistant
              </h1>
              <p className="text-purple-100 text-sm">
                {subcategories.find(s => s.slug === subcategory)?.name || subcategory?.replace(/-/g, ' ')}
                <span className="opacity-70"> • {categories.find(c => c.id === category)?.name}</span>
              </p>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Image Upload Section - Always at Top */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Upload className="h-5 w-5 text-purple-600" />
                    Product Images
                  </CardTitle>
                  <CardDescription>
                    Upload photos first - AI will analyze them to auto-fill details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <label
                    htmlFor="ai-chat-image-upload"
                    className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer block ${
                      images.length > 0 ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-purple-400'
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      id="ai-chat-image-upload"
                      disabled={uploadingImages}
                    />
                    {uploadingImages ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-10 w-10 text-purple-500 animate-spin mb-2" />
                        <p className="text-gray-600">Uploading & Analyzing...</p>
                      </div>
                    ) : images.length > 0 ? (
                      <div className="flex flex-col items-center">
                        <CheckCircle className="h-10 w-10 text-green-500 mb-2" />
                        <p className="text-green-700 font-medium">{images.length} image(s) uploaded</p>
                        <p className="text-sm text-gray-500">Click to add more</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <ImageIcon className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-gray-600 font-medium">Click to upload images</p>
                        <p className="text-sm text-gray-400">Max 5 images</p>
                      </div>
                    )}
                  </label>
                  
                  {/* Uploaded Images Preview */}
                  {images.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mt-4">
                      {images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={`${API_URL}${img}`}
                            alt={`Upload ${idx + 1}`}
                            className="w-full h-16 object-cover rounded-lg border"
                          />
                          <button
                            onClick={() => setImages(images.filter((_, i) => i !== idx))}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* AI Analysis Results */}
                  {imageAnalysis && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-purple-900 flex items-center gap-2 text-sm mb-2">
                        <Eye className="h-4 w-4" />
                        AI Detected
                      </h4>
                      <div className="text-sm space-y-1">
                        {imageAnalysis.description && (
                          <p><span className="text-gray-500">Item:</span> <span className="font-medium">{imageAnalysis.description}</span></p>
                        )}
                        {imageAnalysis.brands && imageAnalysis.brands.length > 0 && (
                          <p><span className="text-gray-500">Brand:</span> <span className="font-medium">{imageAnalysis.brands.join(', ')}</span></p>
                        )}
                        {imageAnalysis.tags && imageAnalysis.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {imageAnalysis.tags.slice(0, 5).map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {typeof tag === 'object' ? tag.name : tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Tone Selection - Show all 13 tones, disable unavailable ones */}
              {showToneButtons && !selectedTone && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Wand2 className="h-5 w-5 text-purple-600" />
                      Select Writing Tone
                      {/* Membership Badge */}
                      <Badge 
                        variant={membershipType === 'premium' ? 'default' : membershipType === 'basic' ? 'secondary' : 'outline'}
                        className={`ml-auto text-xs ${
                          membershipType === 'premium' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                          membershipType === 'basic' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {membershipType === 'premium' ? '👑 Premium' : membershipType === 'basic' ? '⭐ Basic' : '🆓 Free'}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="flex items-center justify-between">
                      <span>Choose the tone for your ad content • {availableTones.length} of 13 tones available</span>
                      <span className="text-xs text-gray-500">
                        AI Ads: {aiAdsRemaining}/{aiAdsLimit} remaining
                      </span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {ALL_THIRTEEN_TONES.map((tone, idx) => {
                        const isAvailable = availableTones.includes(tone);
                        const isRestricted = !isAvailable;
                        return (
                          <Button
                            key={idx}
                            variant={isAvailable ? "outline" : "ghost"}
                            className={`relative ${
                              isAvailable 
                                ? 'hover:bg-purple-50 hover:border-purple-400 border-purple-200' 
                                : 'opacity-60 bg-gray-100 border-gray-200 cursor-pointer'
                            }`}
                            onClick={() => {
                              if (isAvailable) {
                                handleToneSelect(tone);
                              } else {
                                // Show upgrade modal for locked tones
                                setUpgradeReason(`Unlock "${tone}" tone and ${13 - availableTones.length} more tones`);
                                setShowUpgradeModal(true);
                              }
                            }}
                            disabled={isLoading}
                          >
                            <span className={isRestricted ? 'text-gray-400' : ''}>{tone}</span>
                            {isRestricted && (
                              <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                🔒
                              </span>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                    {availableTones.length < 13 && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-purple-800 flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            <span>
                              <strong>Upgrade</strong> to unlock all 13 tones and create more engaging ads!
                            </span>
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-purple-700 border-purple-300 hover:bg-purple-100"
                            onClick={() => {
                              setUpgradeReason('Get access to all 13 professional writing tones');
                              setShowUpgradeModal(true);
                            }}
                          >
                            View Plans
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Questions Section - Show after tone is selected */}
              {selectedTone && questions.length > 0 && !isComplete && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-5 w-5 text-purple-600" />
                      Product Details
                      <Badge variant="outline" className="ml-2">{selectedTone}</Badge>
                    </CardTitle>
                    <CardDescription>
                      Answer the questions below. AI-detected values are pre-filled. Use &quot;N/A&quot; for unknown fields.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {questions.map((q, idx) => {
                      // Check if AI analysis can auto-fill this question
                      let autoFillValue = '';
                      let isAutoFilled = false;
                      const qLower = q.text.toLowerCase();
                      
                      if (imageAnalysis) {
                        // Brand detection
                        if (qLower.includes('brand') && imageAnalysis.brands?.length > 0) {
                          autoFillValue = imageAnalysis.brands[0];
                          isAutoFilled = true;
                        }
                        // Model detection  
                        else if (qLower.includes('model') && imageAnalysis.model) {
                          autoFillValue = imageAnalysis.model;
                          isAutoFilled = true;
                        }
                        // Color detection
                        else if (qLower.includes('color') || qLower.includes('colour')) {
                          if (imageAnalysis.colors?.length > 0) {
                            autoFillValue = imageAnalysis.colors[0];
                            isAutoFilled = true;
                          } else if (imageAnalysis.dominant_color) {
                            autoFillValue = imageAnalysis.dominant_color;
                            isAutoFilled = true;
                          }
                        }
                        // Condition detection
                        else if (qLower.includes('condition') && imageAnalysis.condition) {
                          autoFillValue = imageAnalysis.condition;
                          isAutoFilled = true;
                        }
                        // Storage capacity
                        else if ((qLower.includes('storage') || qLower.includes('capacity')) && imageAnalysis.storage) {
                          autoFillValue = imageAnalysis.storage;
                          isAutoFilled = true;
                        }
                        // Description from tags
                        else if (qLower.includes('description') && imageAnalysis.description) {
                          autoFillValue = imageAnalysis.description;
                          isAutoFilled = true;
                        }
                      }
                      
                      // Check if this is a mandatory question (first 5 questions are usually important)
                      const isMandatory = idx < 5;
                      
                      // Get default value for optional fields
                      let defaultOptionalValue = 'Not specified';
                      if (!isMandatory) {
                        if (qLower.includes('battery')) defaultOptionalValue = '90%';
                        else if (qLower.includes('screen')) defaultOptionalValue = 'Good condition';
                        else if (qLower.includes('box') || qLower.includes('accessories')) defaultOptionalValue = 'Yes';
                        else if (qLower.includes('warranty')) defaultOptionalValue = 'No warranty';
                        else if (qLower.includes('imei')) defaultOptionalValue = 'Yes';
                        else if (qLower.includes('reason')) defaultOptionalValue = 'Upgrading';
                        else if (qLower.includes('year') || qLower.includes('purchase')) defaultOptionalValue = '2023';
                        else if (qLower.includes('description') || qLower.includes('detailed')) defaultOptionalValue = 'Well maintained item in good condition';
                        else if (qLower.includes('price type') || qLower.includes('negotiable')) defaultOptionalValue = 'Negotiable';
                        else if (qLower.includes('availability') || qLower.includes('available')) defaultOptionalValue = 'Available now';
                      }
                      
                      const currentValue = questionAnswers[q.id];
                      const displayValue = currentValue !== undefined ? currentValue : (autoFillValue || (isMandatory ? '' : defaultOptionalValue));
                      
                      return (
                        <div key={q.id} className="space-y-1">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              isMandatory 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              Q{q.id}
                            </span>
                            {parseMarkdownBold(q.text.replace(/^\d+\.\s*/, ''))}
                            {isAutoFilled && (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300 ml-auto">
                                ✨ AI Detected
                              </Badge>
                            )}
                          </Label>
                          <Input
                            placeholder={isMandatory ? "Required - Enter your answer" : "Optional - Enter answer or leave as N/A"}
                            value={displayValue}
                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                            className={`bg-white ${isAutoFilled ? 'border-green-300 bg-green-50' : ''}`}
                          />
                        </div>
                      );
                    })}
                    
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={submitAllAnswers}
                        disabled={isLoading}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating Ad...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate Ad
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Generated Ad Section - Show when complete */}
              {isComplete && formData.title && (
                <Card className="border-2 border-green-400">
                  <CardHeader className="pb-3 bg-green-50">
                    <CardTitle className="flex items-center gap-2 text-lg text-green-800">
                      <CheckCircle className="h-5 w-5" />
                      Your Ad is Ready!
                    </CardTitle>
                    <CardDescription>
                      Review and edit your ad before posting
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-4">
                    {/* Title */}
                    <div className="space-y-1">
                      <Label className="flex items-center justify-between">
                        <span className="font-medium">Title</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateField('title')}
                          disabled={regeneratingField === 'title'}
                          className="h-7 text-xs text-purple-600 hover:text-purple-700"
                        >
                          {regeneratingField === 'title' ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          Regenerate
                        </Button>
                      </Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="font-medium"
                      />
                    </div>
                    
                    {/* Description */}
                    <div className="space-y-1">
                      <Label className="flex items-center justify-between">
                        <span className="font-medium">Description</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateField('description')}
                          disabled={regeneratingField === 'description'}
                          className="h-7 text-xs text-purple-600 hover:text-purple-700"
                        >
                          {regeneratingField === 'description' ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          Regenerate
                        </Button>
                      </Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={6}
                        className="resize-none"
                      />
                    </div>
                    
                    {/* SEO Tags */}
                    <div className="space-y-2">
                      <Label className="flex items-center justify-between">
                        <span className="font-medium flex items-center gap-2">
                          <Hash className="h-4 w-4" />
                          SEO Tags
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateField('tags')}
                          disabled={regeneratingField === 'tags'}
                          className="h-7 text-xs text-purple-600 hover:text-purple-700"
                        >
                          {regeneratingField === 'tags' ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          Regenerate
                        </Button>
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {(formData.tags || seoTags || []).map((tag, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary"
                            className="px-2 py-1 flex items-center gap-1"
                          >
                            #{tag}
                            <button
                              onClick={() => {
                                const newTags = (formData.tags || seoTags).filter((_, i) => i !== idx);
                                setFormData(prev => ({ ...prev, tags: newTags }));
                                setSeoTags(newTags);
                              }}
                              className="ml-1 text-gray-500 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add custom tag..."
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newTag.trim()) {
                              const currentTags = formData.tags || seoTags || [];
                              const updatedTags = [...currentTags, newTag.trim()];
                              setFormData(prev => ({ ...prev, tags: updatedTags }));
                              setSeoTags(updatedTags);
                              setNewTag('');
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (newTag.trim()) {
                              const currentTags = formData.tags || seoTags || [];
                              const updatedTags = [...currentTags, newTag.trim()];
                              setFormData(prev => ({ ...prev, tags: updatedTags }));
                              setSeoTags(updatedTags);
                              setNewTag('');
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                    
                    {/* Price and Location */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="font-medium">Price (LKR)</Label>
                        <Input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                          placeholder="Enter price"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="font-medium">Location</Label>
                        <Input
                          value={formData.location}
                          onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="e.g., Colombo"
                        />
                      </div>
                    </div>
                    
                    {/* Condition and Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="font-medium">Condition</Label>
                        <Select
                          value={formData.condition}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, condition: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Brand New">Brand New</SelectItem>
                            <SelectItem value="Like New">Like New</SelectItem>
                            <SelectItem value="Good">Good</SelectItem>
                            <SelectItem value="Fair">Fair</SelectItem>
                            <SelectItem value="For Parts">For Parts</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="font-medium">Phone Number</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+94..."
                        />
                      </div>
                    </div>
                    
                    {/* Regeneration Info */}
                    <div className="flex items-center justify-between text-sm pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">
                          Regenerations: <strong>{regenerationsUsed}</strong> used
                          {freeRegensRemaining > 0 && (
                            <span className="text-green-600"> • {freeRegensRemaining} free remaining</span>
                          )}
                          {extraRegenerations > 0 && (
                            <span className="text-purple-600"> • {extraRegenerations} extra credits</span>
                          )}
                        </span>
                      </div>
                      {freeRegensRemaining <= 0 && extraRegenerations <= 0 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-xs text-amber-600 border-amber-300"
                          onClick={() => {
                            setUpgradeReason('Purchase more regeneration credits');
                            setShowUpgradeModal(true);
                          }}
                        >
                          Buy Credits (Rs.{extraRegenCost}/each)
                        </Button>
                      )}
                    </div>
                    
                    {/* Post Button */}
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsComplete(false);
                          setFormData({ title: '', description: '', price: '', location: '', condition: '', phone: '', tags: [], category_fields: {} });
                          setSeoTags([]);
                        }}
                      >
                        Start Over
                      </Button>
                      <Button
                        onClick={() => setShowConfirmPost(true)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Review & Post Ad
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Chat Messages - Show conversation history */}
              {messages.length > 0 && !isComplete && !showToneButtons && !questions.length && (
                <Card>
                  <CardContent className="p-4">
                    <ScrollArea className="h-[300px] pr-4">
                      {messages.map((msg, idx) => (
                        <div key={idx} className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-lg p-3 ${
                            msg.role === 'user' 
                              ? 'bg-purple-600 text-white' 
                              : msg.isError
                                ? 'bg-red-50 text-red-800 border border-red-200'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              {msg.role === 'user' ? (
                                <User className="h-4 w-4" />
                              ) : (
                                <Bot className="h-4 w-4" />
                              )}
                              <span className="text-xs opacity-70">
                                {msg.role === 'user' ? 'You' : 'AI Assistant'}
                              </span>
                            </div>
                            <div className="text-sm whitespace-pre-wrap">
                              {msg.content.split('\n').map((line, i) => (
                                <p key={i} className="mb-1">{parseMarkdownBold(line)}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      {isLoading && (
                        <div className="flex justify-start mb-4">
                          <div className="bg-gray-100 rounded-lg p-3">
                            <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Right Column: Preview & Price Suggestions */}
            <div className="space-y-6">
              {/* AI Price Suggestion - Show when questions are visible or image uploaded */}
              {(selectedTone || formData.title || imageAnalysis || images.length > 0) && (
                <AIPricingSuggestion
                  category={category}
                  subcategory={subcategory}
                  title={formData.title || imageAnalysis?.description || subcategory?.replace(/-/g, ' ') || ''}
                  condition={formData.condition || 'Good'}
                  description={formData.description || ''}
                  onPriceSelect={(price) => setFormData(prev => ({ ...prev, price: price.toString() }))}
                />
              )}
              
              {/* Ad Preview Card */}
              {(images.length > 0 || formData.title) && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Eye className="h-5 w-5 text-purple-600" />
                      Ad Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {images.length > 0 && (
                      <img
                        src={`${API_URL}${images[0]}`}
                        alt="Preview"
                        className="w-full h-40 object-cover rounded-lg mb-3"
                      />
                    )}
                    {formData.title && (
                      <h3 className="font-semibold text-lg mb-2">{formData.title}</h3>
                    )}
                    {formData.price && (
                      <p className="text-2xl font-bold text-green-600 mb-2">
                        LKR {parseInt(formData.price).toLocaleString()}
                      </p>
                    )}
                    {formData.description && (
                      <p className="text-sm text-gray-600 line-clamp-4">{formData.description}</p>
                    )}
                    {formData.location && (
                      <p className="text-sm text-gray-500 mt-2">📍 {formData.location}</p>
                    )}
                    {formData.condition && (
                      <Badge variant="outline" className="mt-2">{formData.condition}</Badge>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Tips Card */}
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm text-amber-800">
                    <Lightbulb className="h-4 w-4" />
                    Tips for Better Ads
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-amber-700 space-y-1">
                  <p>• Upload clear, well-lit photos</p>
                  <p>• Be honest about condition</p>
                  <p>• Set competitive prices</p>
                  <p>• Include all relevant details</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        
        {/* Post Confirmation Dialog */}
        <Dialog open={showConfirmPost} onOpenChange={setShowConfirmPost}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Ready to Post Your Ad?</DialogTitle>
              <DialogDescription>
                Review your ad details before publishing.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {images.length > 0 && (
                <img
                  src={`${API_URL}${images[0]}`}
                  alt="Ad"
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}
              <div>
                <h3 className="font-semibold text-lg">{formData.title}</h3>
                <p className="text-2xl font-bold text-green-600">
                  LKR {formData.price ? parseInt(formData.price).toLocaleString() : '0'}
                </p>
              </div>
              <p className="text-sm text-gray-600 line-clamp-3">{formData.description}</p>
              <div className="flex gap-4 text-sm text-gray-500">
                <span>📍 {formData.location || 'Not set'}</span>
                <span>📱 {formData.phone || 'Not set'}</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmPost(false)}>
                Edit More
              </Button>
              <Button
                onClick={handlePostAd}
                disabled={isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Post Ad Now
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Upgrade Membership Modal */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Upgrade Your Membership
              </DialogTitle>
              <DialogDescription>
                {upgradeReason || 'Unlock more features with a premium membership'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Current Plan */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Current Plan</p>
                <p className="font-semibold capitalize flex items-center gap-2">
                  {membershipType === 'premium' ? '👑' : membershipType === 'basic' ? '⭐' : '🆓'}
                  {membershipType} Plan
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {availableTones.length} tones • {freeRegensRemaining} free regenerations per ad
                </p>
              </div>
              
              {/* Upgrade Options */}
              <div className="space-y-3">
                {membershipType === 'free' && (
                  <>
                    <div className="p-4 border-2 border-purple-200 rounded-lg hover:border-purple-400 cursor-pointer transition-colors"
                         onClick={() => navigate('/membership')}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-purple-700">⭐ Basic Plan</h4>
                          <p className="text-sm text-gray-600 mt-1">5 tones • 3 free regenerations</p>
                          <p className="text-sm text-gray-600">50 AI Ads per month</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">Rs. 499</p>
                          <p className="text-xs text-gray-500">/month</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-2 border-amber-200 bg-amber-50 rounded-lg hover:border-amber-400 cursor-pointer transition-colors"
                         onClick={() => navigate('/membership')}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-amber-700">👑 Premium Plan</h4>
                          <p className="text-sm text-gray-600 mt-1">10 tones • 5 free regenerations</p>
                          <p className="text-sm text-gray-600">Unlimited AI Ads</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">Rs. 999</p>
                          <p className="text-xs text-gray-500">/month</p>
                          <Badge className="bg-amber-500 text-white text-xs mt-1">Best Value</Badge>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {membershipType === 'basic' && (
                  <div className="p-4 border-2 border-amber-200 bg-amber-50 rounded-lg hover:border-amber-400 cursor-pointer transition-colors"
                       onClick={() => navigate('/membership')}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-amber-700">👑 Upgrade to Premium</h4>
                        <p className="text-sm text-gray-600 mt-1">All 10 tones • 5 free regenerations</p>
                        <p className="text-sm text-gray-600">Unlimited AI Ads • Priority Support</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">Rs. 999</p>
                        <p className="text-xs text-gray-500">/month</p>
                        <Badge className="bg-amber-500 text-white text-xs mt-1">Recommended</Badge>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Regeneration Bundles */}
              {regenerationBundles.length > 0 && (
                <div className="pt-3 border-t">
                  <p className="text-sm font-medium mb-2">Or buy regeneration credits:</p>
                  <div className="flex gap-2 flex-wrap">
                    {regenerationBundles.map((bundle, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => navigate('/membership')}
                      >
                        {bundle.count} regens for Rs.{bundle.price}
                        {bundle.discount && <span className="ml-1 text-green-600">({bundle.discount})</span>}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
                Maybe Later
              </Button>
              <Button 
                className="bg-gradient-to-r from-purple-600 to-pink-600"
                onClick={() => navigate('/membership')}
              >
                View All Plans
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Manual Mode / Preview Mode
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={() => setMode('select')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">
              {mode === 'preview' ? 'Review Your Advertisement' : 'Create Advertisement'}
            </h1>
            <p className="text-blue-100 text-sm">
              {mode === 'preview' ? 'Review and edit before submitting' : 'Fill in the details manually'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Image Upload Section */}
            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Images * ({images.length}/5)
              </Label>
              <p className="text-sm text-gray-500 mb-3">Upload at least 1 image (max 5)</p>
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              <div className="grid grid-cols-5 gap-3">
                {images.map((img, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={`${API_URL}${img}`}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImages}
                    className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    {uploadingImages ? (
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">Add Photo</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Title *</Label>
                  {composerContent?.title_alternatives && (
                    <div className="flex gap-1">
                      {composerContent.title_alternatives.map((alt, idx) => (
                        <Button
                          key={idx}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, title: alt }));
                            setComposerContent(prev => ({ ...prev, title_alternatives: null }));
                          }}
                        >
                          Option {idx + 1}
                        </Button>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => regenerateField('title')}
                    disabled={regeneratingField === 'title'}
                    className="text-purple-600 h-6"
                  >
                    {regeneratingField === 'title' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    <span className="ml-1 text-xs">Alternatives</span>
                  </Button>
                </div>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter a catchy title for your ad"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Price (Rs) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Enter price"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Colombo, Kandy"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={country === "UK" ? "+44 7XXX XXXXXX" : "+94 7X XXX XXXX"}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Condition</Label>
                <Select
                  value={formData.condition}
                  onValueChange={(value) => setFormData({ ...formData, condition: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Brand New">Brand New</SelectItem>
                    <SelectItem value="Used - Like New">Used - Like New</SelectItem>
                    <SelectItem value="Used - Good">Used - Good</SelectItem>
                    <SelectItem value="Used - Fair">Used - Fair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Category</Label>
                <Input
                  value={categories.find(c => c.id === category)?.name || category}
                  disabled
                  className="mt-1 bg-gray-50"
                />
              </div>
              
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Description *</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => regenerateField('description')}
                    disabled={regeneratingField === 'description'}
                    className="text-purple-600 h-6"
                  >
                    {regeneratingField === 'description' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    <span className="ml-1 text-xs">Regenerate</span>
                  </Button>
                </div>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your item in detail..."
                  rows={6}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Vehicle reg-plate auto-fill (only when a Vehicles category is picked) */}
            {category === 'vehicles' && (
              <VehicleRegLookup value={vehicleSpecs} onChange={setVehicleSpecs} />
            )}

            {/* AI Composer Section */}
            <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Wand2 className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">AI Ad Composer</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {/* Tone Selection */}
                  <div>
                    <Label className="text-xs text-purple-700">Tone</Label>
                    <Select
                      value={selectedComposerTone}
                      onValueChange={setSelectedComposerTone}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casual">😊 Casual</SelectItem>
                        <SelectItem value="professional">👔 Professional</SelectItem>
                        <SelectItem value="urgent">⚡ Urgent</SelectItem>
                        <SelectItem value="luxury">✨ Luxury</SelectItem>
                        <SelectItem value="bargain">💰 Bargain</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Language Selection */}
                  <div>
                    <Label className="text-xs text-purple-700">Language</Label>
                    <Select
                      value={composerLanguage}
                      onValueChange={setComposerLanguage}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">🇬🇧 English</SelectItem>
                        <SelectItem value="si">🇱🇰 සිංහල</SelectItem>
                        <SelectItem value="ta">🇱🇰 தமிழ்</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Generate Button */}
                  <div className="col-span-2 flex items-end gap-2">
                    <Button
                      onClick={generateAIContent}
                      disabled={composerGenerating || !category}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      {composerGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Content
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Translation Buttons */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => translateContent('si')}
                    disabled={composerGenerating || !formData.title}
                    className="text-xs"
                  >
                    <Languages className="h-3 w-3 mr-1" />
                    Translate to සිංහල
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => translateContent('ta')}
                    disabled={composerGenerating || !formData.title}
                    className="text-xs"
                  >
                    <Languages className="h-3 w-3 mr-1" />
                    Translate to தமிழ்
                  </Button>
                </div>
                
                {/* Generated Content Preview */}
                {composerContent && (
                  <div className="space-y-3">
                    {/* Highlights */}
                    {composerContent.highlights && composerContent.highlights.length > 0 && (
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-medium">Key Highlights</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {composerContent.highlights.map((h, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {h}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Tags */}
                    {composerContent.tags && composerContent.tags.length > 0 && (
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Hash className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium">Suggested Tags</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => regenerateField('tags')}
                            disabled={regeneratingField === 'tags'}
                            className="h-6 text-xs"
                          >
                            {regeneratingField === 'tags' ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {composerContent.tags.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* SEO Tips */}
                    {composerContent.seo_tips && composerContent.seo_tips.length > 0 && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-800">SEO Tips</span>
                        </div>
                        <ul className="text-xs text-yellow-700 space-y-1">
                          {composerContent.seo_tips.slice(0, 3).map((tip, i) => (
                            <li key={i}>• {tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="flex gap-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setMode('select')}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleConfirmPost}
                disabled={isLoading || !formData.title || !formData.description}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500"
              >
                <Eye className="h-4 w-4 mr-2" />
                Review & Post
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmPost} onOpenChange={setShowConfirmPost}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Review Before Posting
            </DialogTitle>
            <DialogDescription>
              Please review your advertisement carefully before submitting.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Images Preview */}
            {images.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <img
                    key={idx}
                    src={`${API_URL}${img}`}
                    alt={`Preview ${idx + 1}`}
                    className="h-20 w-20 object-cover rounded-lg border"
                  />
                ))}
              </div>
            )}
            
            {/* Title */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <Label className="text-xs text-gray-500">Title</Label>
              <p className="font-semibold text-lg">{formData.title}</p>
            </div>
            
            {/* Price & Location */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500">Price</Label>
                <p className="font-semibold">Rs. {formData.price || 'Negotiable'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500">Location</Label>
                <p className="font-semibold">{formData.location || 'Sri Lanka'}</p>
              </div>
            </div>
            
            {/* Description */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <Label className="text-xs text-gray-500">Description</Label>
              <p className="text-sm whitespace-pre-wrap mt-1">{formData.description}</p>
            </div>
            
            {/* Category & Condition */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500">Category</Label>
                <p className="text-sm">{categories.find(c => c.id === category)?.name || category}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500">Condition</Label>
                <p className="text-sm">{formData.condition || 'Not specified'}</p>
              </div>
            </div>
            
            {/* Contact */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-xs text-blue-600">Contact Number</Label>
              <p className="font-semibold text-blue-900">{formData.phone}</p>
            </div>
            
            {/* Warning */}
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Before you post:</p>
                <ul className="mt-1 text-xs space-y-1">
                  <li>• Make sure all information is accurate</li>
                  <li>• Check your contact number is correct</li>
                  <li>• Review images are clear and relevant</li>
                  <li>• Click &quot;Edit&quot; below to make changes</li>
                </ul>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmPost(false)}
            >
              <PenLine className="h-4 w-4 mr-2" />
              Edit Ad
            </Button>
            <Button
              onClick={() => {
                setShowConfirmPost(false);
                submitAd();
              }}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm & Post
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAdAssistant;
